import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FileCheck, X, FileText, FolderOpen, Archive } from "lucide-react";
import ChecklistLetter from "../agreements/ChecklistLetter";
import { DocumentUpload } from "./documents/DocumentUpload";
import { DocumentList } from "./documents/DocumentList";
import { useDealDocuments } from "@/hooks/useDealDocuments";

interface Deal {
  id: string;
  deal_title: string;
  seller_name: string;
  seller_email?: string;
  seller_phone?: string;
  seller_address?: string;
  buyer_name: string;
  buyer_address?: string;
  property_address?: string;
  current_balance?: number;
  payment_amount?: number;
  interest_rate?: number;
  first_payment_date?: string;
  date_of_sale?: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
}

interface DealDocumentsProps {
  deal: Deal;
  onClose: () => void;
}

export function DealDocuments({ deal, onClose }: DealDocumentsProps) {
  const { documents, loading, refetch } = useDealDocuments(deal.id);
  
  const activeDocCount = documents.filter(d => !d.is_archived).length;
  const archivedDocCount = documents.filter(d => d.is_archived).length;
  const totalSize = documents.reduce((sum, d) => sum + d.file_size, 0);
  
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deal Documents</h1>
          <p className="text-muted-foreground">
            {deal.deal_title} - {deal.seller_name}
          </p>
        </div>
        <Button variant="outline" onClick={onClose}>
          <X className="h-4 w-4 mr-2" />
          Back to Deals
        </Button>
      </div>

      {/* Deal Summary Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Seller</p>
              <p className="font-medium">{deal.seller_name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Property</p>
              <p className="font-medium">{deal.property_address || 'N/A'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Current Balance</p>
              <p className="font-medium">
                {deal.current_balance 
                  ? `$${deal.current_balance.toLocaleString()}` 
                  : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Monthly Payment</p>
              <p className="font-medium">
                {deal.payment_amount 
                  ? `$${deal.payment_amount.toLocaleString()}` 
                  : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Stats Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
                <p className="text-2xl font-bold">{activeDocCount}</p>
              </div>
              <p className="text-xs text-muted-foreground">Active Documents</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <Archive className="h-4 w-4 text-muted-foreground" />
                <p className="text-2xl font-bold">{archivedDocCount}</p>
              </div>
              <p className="text-xs text-muted-foreground">Archived</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatFileSize(totalSize)}</p>
              </div>
              <p className="text-xs text-muted-foreground">Total Storage</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document Management Tabs */}
      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            Documents
            {activeDocCount > 0 && (
              <Badge variant="secondary" className="ml-1">{activeDocCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="checklist" className="flex items-center gap-2">
            <FileCheck className="h-4 w-4" />
            Document Checklist
          </TabsTrigger>
          <TabsTrigger value="info" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            About Agreements
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-6 space-y-6">
          <DocumentUpload dealId={deal.id} onUploadComplete={refetch} />
          {loading ? (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                Loading documents...
              </CardContent>
            </Card>
          ) : (
            <DocumentList dealId={deal.id} documents={documents} onRefresh={refetch} />
          )}
        </TabsContent>

        <TabsContent value="checklist" className="mt-6">
          <ChecklistLetter preLoadedDeal={deal} />
        </TabsContent>

        <TabsContent value="info" className="mt-6">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Purchase Agreement Generator</h3>
                <p className="text-muted-foreground">
                  For creating full purchase agreements with this deal's data, please use the 
                  dedicated Agreements page from the admin sidebar.
                </p>
                <p className="text-sm text-muted-foreground">
                  The Agreements page provides advanced features including:
                </p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>AI-powered agreement generation</li>
                  <li>Full and partial purchase agreements</li>
                  <li>Deal selection from database</li>
                  <li>Financial breakdown calculations</li>
                  <li>Professional document formatting</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
